import { Grid, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";

import {
  PhysicalExamItemAnswer,
  PhysicalExamNotesAnswer,
  PhysicalExamSectionValues,
  PhysicalExamsGrid,
  PhysicalExamSingleChoiceValues,
} from "../types/physical-exams";

import PhysicalExamsDiffColumn from "./PhysicalExamsDiffColumn";

const PhysicalExamsDiffGrid = ({ title, histories }: PhysicalExamsGrid) => {
  const historyData = histories[title];
  if (!historyData) return null;

  return historyData.map(([currentAnswers, previousAnswers], index) => {
    if (currentAnswers?.type === "section") {
      const previous = previousAnswers as PhysicalExamItemAnswer;
      const current = currentAnswers as PhysicalExamItemAnswer;

      const isSectionValues = (
        items: PhysicalExamSectionValues | PhysicalExamSingleChoiceValues | undefined,
      ): items is PhysicalExamSectionValues => {
        return items !== undefined && "normalDetails" in items;
      };

      const isSingleChoiceValues = (
        items: PhysicalExamSectionValues | PhysicalExamSingleChoiceValues | undefined,
      ): items is PhysicalExamSingleChoiceValues => {
        return items !== undefined && "singleChoiceDetails" in items;
      };

      return (
        <Grid p={24} key={`${title}-${index}-grid`}>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Stack>
              <TextWrapper variant="bold">{current?.sectionName}</TextWrapper>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            {current?.items?.declined ? (
              <TextWrapper pb={8} c="dimmed">
                Declined
              </TextWrapper>
            ) : isSectionValues(current?.items) ? (
              <Grid>
                <PhysicalExamsDiffColumn
                  isPrevious
                  showRemoved
                  columnName="Previous Normal"
                  otherSectionDetails={current?.items?.normalDetails}
                  sectionDetails={
                    isSectionValues(previous?.items) ? previous?.items?.normalDetails : undefined
                  }
                />
                <PhysicalExamsDiffColumn
                  columnName="Amend Normal"
                  sectionDetails={current?.items?.normalDetails}
                  otherSectionDetails={
                    isSectionValues(previous?.items) ? previous?.items?.normalDetails : undefined
                  }
                />
                <PhysicalExamsDiffColumn
                  isPrevious
                  showRemoved
                  columnName="Previous Not Normal"
                  otherSectionDetails={current?.items?.notNormalDetails}
                  sectionDetails={
                    isSectionValues(previous?.items) ? previous?.items?.notNormalDetails : undefined
                  }
                />
                <PhysicalExamsDiffColumn
                  columnName="Amended Not Normal"
                  sectionDetails={current?.items?.notNormalDetails}
                  otherSectionDetails={
                    isSectionValues(previous?.items) ? previous?.items?.notNormalDetails : undefined
                  }
                />
              </Grid>
            ) : isSingleChoiceValues(current?.items) ? (
              <Grid>
                <PhysicalExamsDiffColumn
                  isPrevious
                  showRemoved
                  columnName="Previous Single Choice"
                  otherSectionDetails={current?.items?.singleChoiceDetails}
                  sectionDetails={
                    isSingleChoiceValues(previous?.items)
                      ? previous?.items?.singleChoiceDetails
                      : undefined
                  }
                />
                <PhysicalExamsDiffColumn
                  columnName="Amended Single Choice"
                  sectionDetails={current?.items?.singleChoiceDetails}
                  otherSectionDetails={
                    isSingleChoiceValues(previous?.items)
                      ? previous?.items?.singleChoiceDetails
                      : undefined
                  }
                />
              </Grid>
            ) : null}
          </Grid.Col>
        </Grid>
      );
    }

    const previous = previousAnswers as PhysicalExamNotesAnswer;
    const current = currentAnswers as PhysicalExamNotesAnswer;

    return (
      (previous?.notes || current?.notes) && (
        <Grid p={24} key={title}>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <TextWrapper variant="bold">Notes</TextWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Grid>
              <PhysicalExamsDiffColumn
                notesType
                isPrevious
                showRemoved
                columnName="Previous Normal"
                noteDetails={previous?.notes}
                otherNoteDetails={current?.notes}
              />
              <PhysicalExamsDiffColumn
                notesType
                columnName="Amend Normal"
                noteDetails={current?.notes}
                otherNoteDetails={previous?.notes}
              />
            </Grid>
          </Grid.Col>
        </Grid>
      )
    );
  });
};

export default PhysicalExamsDiffGrid;
